import 'lazysizes/plugins/unveilhooks/ls.unveilhooks'; // it's necessary for bg-image support !!!

//import '../global.d.ts';
import { Collapse } from 'bootstrap';
import * as lazySizes from 'lazysizes'; // https://github.com/aFarkas/lazysizes/blob/gh-pages/README.md

import { trackCustomEvent } from '../helpers/analytics';
import { markInvalidControls } from '../helpers/forms';
import { addClass, getClosesParent, hasClass, isDataFileLink, isEmailLink, isExternalLink } from '../helpers/generic';
import { scrollToSelector } from '../helpers/scroll';

// nastaveni lazy-loadu na specifickou classu
lazySizes.cfg.lazyClass = 'img--lazy';

// adds GATC custom event to the email links + specific class
export const processEmailLinks = (parentEl?: HTMLElement): void => {
    const parent = parentEl ?? document.body;

    const possibleEmailLinks = parent.querySelectorAll('a');
    for (const linkEl of Array.from(possibleEmailLinks)) {
        const link = linkEl as HTMLLinkElement | HTMLAnchorElement;
        if (isEmailLink(link)) {
            addClass(link, 'link-email');
            const href = link.getAttribute('href');
            trackCustomEvent('click', {
                link_classes: link.getAttribute('class'),
                link_domain: href ? href.slice(href.lastIndexOf('@') + 1) : '',
                link_id: link.getAttribute('id'),
                link_url: href ? href.slice(href.lastIndexOf(':') + 1) : '',
                outbound: true,
            });
        }
    }
};

// add custom functionality to the external links + specific class
export const processExternalLinks = (parentEl?: HTMLElement): void => {
    const parent = parentEl ?? document.body;

    const possibleExternalLinks = parent.querySelectorAll('.page-content a:not(:has(img))');
    for (const linkEl of Array.from(possibleExternalLinks)) {
        const link = linkEl as HTMLLinkElement | HTMLAnchorElement;
        if (isExternalLink(link)) {
            addClass(link, 'link-external');
            if (!hasClass(link, 'btn')) {
                const iconEl = document.createElement('sup');
                iconEl.innerHTML =
                    '<svg height="24" viewBox="0 0 1792 1792" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M1408 928v-480q0-26-19-45t-45-19h-480q-42 0-59 39-17 41 14 70l144 144-534 534q-19 19-19 45t19 45l102 102q19 19 45 19t45-19l534-534 144 144q18 19 45 19 12 0 25-5 39-17 39-59zm256-512v960q0 119-84.5 203.5t-203.5 84.5h-960q-119 0-203.5-84.5t-84.5-203.5v-960q0-119 84.5-203.5t203.5-84.5h960q119 0 203.5 84.5t84.5 203.5z"/></svg>';
                iconEl.setAttribute('title', window.__WEBSITE__.AppStrings.LNG_external_link_title ?? '');
                iconEl.setAttribute('class', 'link-external__icon');
                link.appendChild(iconEl);
            }
            link.addEventListener('click', function (this: HTMLAnchorElement, e: MouseEvent) {
                e.preventDefault();
                window.open(this.href);
                return false;
            });
        }
    }
};

// add slow scroll functionality to the anchor links
export const processAnchorLinks = (parentEl?: HTMLElement): void => {
    const parent = parentEl ?? document.body;

    const possibleAnchorLinks = parent.querySelectorAll('a[href^="#"]');
    for (const linkEl of Array.from(possibleAnchorLinks)) {
        const link = linkEl as HTMLLinkElement | HTMLAnchorElement;
        const disabledAttr = link.getAttribute('data-disable-scroll');
        const dataToggleAttr = link.getAttribute('data-bs-toggle');
        const changeUrlAttr = link.getAttribute('data-change-url');

        const isDisabled = disabledAttr && ['true', '1', 'yes'].includes(disabledAttr);
        const isBsToggler = dataToggleAttr && ['true', '1', 'yes'].includes(dataToggleAttr);
        const addAnchorHashToUrl = changeUrlAttr && ['true', '1', 'yes'].includes(changeUrlAttr);

        if (!isDisabled && !isBsToggler) {
            const anchor = link.getAttribute('href');
            if (anchor && anchor.length < 2) {
                return;
            }
            // slow scroll to the anchor element:
            if (anchor) {
                link.addEventListener('click', function (e) {
                    e.preventDefault();
                    scrollToSelector(anchor);
                    trackCustomEvent('select_content', {
                        content_type: 'anchor_element',
                        item_id: anchor,
                    });
                    // close EXPANDED only menus:
                    const collapseElementList = Array.from(document.querySelectorAll('.navbar-collapse.show'));
                    collapseElementList.forEach(function (collapseEl: Element) {
                        const menu = new Collapse(collapseEl);
                        menu.hide();
                    });
                    // Update the URL hash
                    if (addAnchorHashToUrl) {
                        history.pushState(null, '', anchor);
                    }
                    return false; // to prevent default action
                });
            }
        }
    }
};

// opens data file links in the new tab/window + adds specific class
export const processDataLinks = (parentEl?: HTMLElement): void => {
    const parent = parentEl ?? document.body;

    const possibleDataLinks = parent.querySelectorAll('a');
    for (const linkEl of Array.from(possibleDataLinks)) {
        const link = linkEl as HTMLLinkElement | HTMLAnchorElement;
        // QR / PhotoSwipe — neotevírat v novém tabu (řeší lightbox)
        if (hasClass(link, 'business-card__qr-link') || link.dataset['pswpBound'] === '1') {
            continue;
        }
        if (isDataFileLink(link)) {
            addClass(link, 'link-datafile');
            link.addEventListener('click', function (this: HTMLAnchorElement, e: MouseEvent) {
                e.preventDefault();
                window.open(this.href);
                return false;
            });
        }
    }
};

// opens data file links in the new tab/window
export const processNewTabLinks = (parentEl?: HTMLElement): void => {
    const parent = parentEl ?? document.body;

    const possibleNewTabLinks = parent.querySelectorAll('a[data-target="new-window"]');
    for (const linkEl of Array.from(possibleNewTabLinks)) {
        const link = linkEl as HTMLLinkElement | HTMLAnchorElement;
        addClass(link, 'link-new-window');
        link.addEventListener('click', function (this: HTMLAnchorElement, e: MouseEvent) {
            e.preventDefault();
            window.open(this.href);
            return false;
        });
    }
};

// makes responsive tables
export const processTables = (parentEl?: HTMLElement): void => {
    const parent = parentEl ?? document.body;

    const tables = parent.querySelectorAll('table');
    for (const table of Array.from(tables)) {
        const parentDiv = getClosesParent(table, function (el: HTMLElement) {
            return el.tagName.toUpperCase() === 'DIV';
        });
        if (!parentDiv || !hasClass(parentDiv as HTMLDivElement, 'table-responsive')) {
            // create wrapper container
            const wrapper = document.createElement('div');
            wrapper.setAttribute('class', 'table-responsive');

            // insert wrapper before el in the DOM tree
            const tableParent = table.parentNode;
            if (tableParent) {
                tableParent.insertBefore(wrapper, table);
                wrapper.appendChild(table);
            }
        }
    }
};

/**
 * Zprocesuje obsah stránky a aplikuje všemožné funkcionality. Objektem předaným v parametru lze omezit aplikaci pouze na potomky tohoto objektu.
 *
 * @param parentEl
 */
export const processPageContent = (parentEl?: HTMLElement): void => {
    const parent = parentEl ?? document.body;

    processEmailLinks(parent);
    processExternalLinks(parent);
    processAnchorLinks(parent);
    processDataLinks(parent);
    processNewTabLinks(parent);

    // lazy load of images and background images (blur-up is opt-in via markup class)
    lazySizes.loader.checkElems(true);

    // make responsive tables:
    processTables(parent);

    // Error Fields
    markInvalidControls('.form-group span.error:not(.d-none)', 'form-group', 'has-error'); // oznaceni chybovych policek

    // FormMaker dialog — přesunout na body (mimo .page-content / navbar stacking context)
    const formMakerModalElement = document.querySelector('#formMakerStatusMsg');
    if (formMakerModalElement instanceof HTMLElement && window.__WEBSITE__?.App?.Bootstrap?.Modal) {
        if (formMakerModalElement.parentElement !== document.body) {
            document.body.appendChild(formMakerModalElement);
        }
        const bsMsgModal = window.__WEBSITE__.App.Bootstrap.Modal.getOrCreateInstance(formMakerModalElement);
        if (bsMsgModal) {
            bsMsgModal.show();
        }
    }
};
